import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { NotificationMessage } from '@models/notificationMessage.class';
import { Store } from '@ngrx/store';
import { AppManagementService } from '@services/appManagement.service';
import { SecurityService } from '@services/security.service';
import {
  interval,
  Observable,
  Subscription,
  throwError as observableThrowError,
} from 'rxjs';
import { catchError, filter, map, take } from 'rxjs/operators';
import {
  AppState,
  CURRENT_USER_ROLE,
  EL_PATRON_SHOW_IFRAME,
  LOGIN_USER,
  LOGOUT_USER,
  TRACKING_EVENTS,
} from 'src/app';
import { config } from 'src/app/environment-config';
import { MixpanelService } from 'src/core/services/mixpanel.service';
import { NotificationService } from 'src/core/services/notification.service';
import { StorageService } from 'src/core/services/storage.service';
import { decomposeJWT } from 'src/shared/utility/miscellaneous';
import { SharedSolaceMainService } from 'src/solace/';
import { AuthService } from '../services/auth-service.service';

const { timeOuts_timeOut1Min } = config();

@Injectable()
export class SessionService {
  private sessionStatusSubscription: Subscription;
  private elPatronData: any = {};
  private tokens: any = {};
  private mfaType = '';
  private expirationInterval: Subscription;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private store: Store<AppState>,
    private storageService: StorageService,
    private securityRestService: SecurityService,
    private notificationService: NotificationService,
    private sharedSolaceMainService: SharedSolaceMainService,
    private appManagementRestService: AppManagementService,
    private mixpanelService: MixpanelService,
    private authService: AuthService,
  ) {
    this.store
      .select('elPatronData')
      .pipe(
        map((value: any) => value.toJS()),
        filter((value: any) => value.URL_SIGNIN !== undefined),
        take(1),
      )
      .subscribe((value: any) => {
        this.elPatronData = value;
      });
  }

  login(validToken: string, idToken: string): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.securityRestService
        .getUser()
        .toPromise()
        .then(
          (result: any) => {
            result.sessionToken = validToken;
            result.hash = validToken;
            result.isReadAndWrite = true;
            result.mfaType = this.mfaType;
            result.idToken = idToken;

            this.tokens.userToken = validToken;

            this.setLocalStorage(result);

            this.sharedSolaceMainService.updateHash(validToken);

            this.store.dispatch({
              type: LOGIN_USER,
              payload: result,
            });

            this.getUserRole();

            resolve(result);
          },
          (error: any) => {
            const notificationMessage: NotificationMessage =
              new NotificationMessage();

            notificationMessage.eventId = error.EventId;
            notificationMessage.message = `${error.ErrorMessage}`;
            notificationMessage.isBusinessError = error.IsBussinessError;

            if (error.ErrorCode === 3003) {
              this.notificationService.showWarning(notificationMessage);
            } else {
              this.notificationService.showError(notificationMessage);
            }

            reject(error);
          },
        );
    });
  }

  /**
   * @method assignUserRole : Called when service return an array of roles
   *
   * @param roles: Array of user's role in the app.
   */

  assignUserRole(roles: any[]) {
    const role = roles.reduce((prev, current) => {
      return prev.roleId > current.roleId ? prev : current;
    });

    this.mixpanelService.track(
      TRACKING_EVENTS.get_user_role.event,
      TRACKING_EVENTS.get_user_role.success(role.name),
    );

    this.setUserRoleStorage(role.roleId);

    this.store.dispatch({
      type: CURRENT_USER_ROLE,
      payload: role.roleId,
    });
  }

  /**
   * @method getUserRole : Called to know the user's role in the app
   */
  getUserRole() {
    this.appManagementRestService
      .getUserRole()
      .toPromise()
      .then(
        (result: any) => {
          if (result.length > 0) {
            this.assignUserRole(result);
          }
        },
        (error: any) => {
          const notificationMessage: NotificationMessage =
            new NotificationMessage();

          notificationMessage.eventId = error.EventId;
          notificationMessage.message = `${error.ErrorMessage}`;
          notificationMessage.isBusinessError = error.IsBussinessError;

          this.notificationService.showError(notificationMessage);
          this.mixpanelService.track(
            TRACKING_EVENTS.get_user_role.event,
            TRACKING_EVENTS.get_user_role.error(error),
          );
        },
      );
  }

  logout(redirect: boolean = true): void {
    if (this.sessionStatusSubscription) {
      this.sessionStatusSubscription.unsubscribe();
      this.sessionStatusSubscription = undefined;
    }

    this.storageService.clearOne('session');
    this.storageService.clearOne('userrole');
    this.storageService.clearOne('session_first_time');
    this.storageService.clearOne('has_dashboard');
    this.storageService.clearOne('contract_id');
    this.storageService.clearOne('is_upgrade_banner_closed');

    this.store.dispatch({
      type: LOGOUT_USER,
      payload: false,
    });

    this.store.dispatch({
      type: CURRENT_USER_ROLE,
      payload: false,
    });

    if (redirect) {
      this.redirecToSignOut();
    }
  }

  redirecToSignOut() {
    const WAIT_TIME = 100;

    if (this.elPatronData.URL_SIGNOUT !== undefined) {
      this.document.location.href = this.elPatronData.URL_SIGNOUT;
    } else {
      setTimeout(() => {
        this.redirecToSignOut();
      }, WAIT_TIME);
    }
  }

  showSilentIframe(payload: boolean = true) {
    this.store.dispatch({
      type: EL_PATRON_SHOW_IFRAME,
      payload,
    });
  }

  setAuthorizationHeader(tokenString: string) {
    this.authService.setAuthorizationSession(tokenString);
  }

  setIdTokenSession(tokenString: string) {
    this.authService.setIdTokenSession(tokenString);
  }

  getSessionToken() {
    const currentSession = this.storageService.getItem('session', true);
    return currentSession && currentSession.sessionToken;
  }

  getIdToken() {
    const currentSession = this.storageService.getItem('session', true);
    return currentSession && currentSession.idToken;
  }

  getUserId() {
    const token = this.getSessionToken();
    const decomposedToken = token ? decomposeJWT(token) : {};
    const userId = decomposedToken.sub;
    return userId;
  }

  getEmail(token?: string) {
    const sessionToken = this.getIdToken() || token;
    const decomposedToken = sessionToken ? decomposeJWT(sessionToken) : {};
    return decomposedToken.email;
  }

  didUserChange(newToken: string, oldToken: string) {
    if (!newToken || !oldToken) {
      return true;
    }

    const decodedUrl = decomposeJWT(newToken);
    const decodedLocalStorage = decomposeJWT(oldToken);
    return decodedUrl.sub !== decodedLocalStorage.sub;
  }

  /**
   * Returns the expiration date of a token.
   *
   * @param token - A JWT token
   * @returns Expiration Date
   */
  getTokenExpiration(token: string, format = false) {
    const decoded = decomposeJWT(token);

    if (!decoded.exp) {
      return;
    }

    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return format ? decoded.exp : date;
  }

  /**
   * Returns false when token is still valid, returns true otherwise.
   *
   * @param token - A JWT token
   */
  isTokenExpired(token: string) {
    if (!token) {
      return;
    }
    const expirationDate = this.getTokenExpiration(token);
    const isTokenExpired =
      expirationDate === undefined
        ? false
        : expirationDate.valueOf() <= new Date().valueOf();
    return isTokenExpired;
  }

  startExpirationInterval(accessToken: string) {
    this.store
      .select('params')
      .pipe(
        map((value: any) => value.toJS()),
        filter(
          (value: any) =>
            value.centralHour !== undefined && value.centralHour.length > 0,
        ),
        take(1),
      )
      .subscribe((value: any) => {
        const timeToExpire = this.getTokenExpiration(accessToken, true);
        const centralHour: number = Math.floor(
          new Date(value.centralHour).getTime() / 1000.0,
        );
        const timeLeft =
          (Math.round((timeToExpire - centralHour) / 60) - 1) *
          +timeOuts_timeOut1Min;
        this.expirationInterval && this.expirationInterval.unsubscribe();
        this.expirationInterval = interval(timeLeft)
          .pipe(take(1))
          .subscribe(() => this.showSilentIframe());
      });
  }

  setSessionDotCom(identityToken: string): Observable<any> {
    return this.securityRestService
      .setSessionDotCom(identityToken)
      .pipe(catchError((error) => this.handleError(error)));
  }

  public setCurrentContract(contractId: string): void {
    this.storageService.setItem('contract_id', contractId);
  }

  public getCurrentContract(): string {
    if (this.storageService.getItem('contract_id')) {
      return this.storageService.getItem('contract_id').replace(/[\\\"]/g, '');
    }

    return undefined;
  }

  private setLocalStorage(data: any): void {
    this.storageService.setItem('session', data);
  }

  /**
   * @method setUserRoleStorage : Set users role in local storage
   */
  private setUserRoleStorage(data: any): void {
    this.storageService.setItem('userrole', data);
  }

  private handleError(error: any) {
    this.notificationService.showServiceError(error);
    return observableThrowError(error);
  }
}
